import * as Papa from 'papaparse';

import Format from 'helpers/format';
import Modules from 'shared_modules/Modules';

import { REPORT_TYPE } from './types';

// List each properly a given report should contain
const REPORT_FILTERS = {
  [REPORT_TYPE.NORMAL]: [
    'shopName',
    'subtotal',
    'discount',
    'vatRate',
    'taxPoint',
    'total',
    'totalTax',
    'paymentDate',
    'netOfVat',
    'description',
    'unitSold',
    'saleID',
  ],
  [REPORT_TYPE.RETAIL]: ['shopName', 'subtotal', 'discount', 'vatRate', 'date', 'total'],
};

export const formatDateForMTDReport = (date: Date) => {
  const d = new Date(date);
  let month = `${d.getMonth() + 1}`;
  let day = `${d.getDate()}`;
  const year = d.getFullYear();

  if (month.length < 2) month = `0${month}`;
  if (day.length < 2) day = `0${day}`;

  return [year, month, day].join('-');
};

export const formatDateCSV = (unformattedDates: TSFixMe) =>
  unformattedDates
    .map((date: TSFixMe) => `${date}T00:00:00-0${new Date().getTimezoneOffset() / 60}:00`)
    .join(',');

/**
 * Convert a date string to a specific timezone
 */
export const applyTimeZone = (date: string, tzString: string): Date => {
  // @ts-expect-error ts-migrate(6133) FIXME: 'strDate' is declared but its value is never read.
  const strDate = (typeof date === 'string' ? new Date(date) : date).toLocaleString('en-CA', {
    timeZone: tzString,
    hour12: false,
  });

  return new Date(
    // (Date constructor only support [RFV 2822 date format](https://datatracker.ietf.org/doc/html/rfc2822#page-14) format)
    // Important to set hour12:false & use en-CA locale to be compatible with RFC 2822
    // Also remove `,`, `/` and multi space characters as they are not supported by RFC 2822
    (typeof date === 'string' ? new Date(date) : date)
      .toLocaleString('en-CA', {
        timeZone: tzString,
        hour12: false,
      })
      .replace(/(,|\s)+/g, ' ')
      .split('/')
      .join('-')
  );
};

export const localizeDate = (date: string, format: string, timeZone: string) => {
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Date' is not assignable to param... Remove this comment to see the full error message
  return Format.formatDateTime(applyTimeZone(date, timeZone), format);
};

export const filterJSON = (inclusionList: TSFixMe, json: TSFixMe) =>
  Object.fromEntries(Object.entries(json).filter((field) => inclusionList.includes(field[0])));

export const generateCSV = async (records: TSFixMe) => {
  return Papa.unparse(records);
};

const toMoney = (value: number, currency: string, locale: string): string => {
  const numberToCurrency = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });
  return numberToCurrency.format(value);
};

export const getSaleRecords = (results: TSFixMe, reportName: REPORT_TYPE) => {
  const userLocale = window?.merchantos?.locale ?? {};
  const currencyCode = userLocale.currency_code ?? 'USD';
  const localeCode = userLocale.lang ?? 'en-US';
  const dateTimeFormat = userLocale.date_time_format ?? 'm/d/Y g:i a';

  return results.reduce((processedSales: TSFixMe, currentSale: TSFixMe) => {
    const shopTimeZone = currentSale?.Shop?.timeZone ?? null;
    const {
      completeTime,
      Shop: { name: shopName },
    } = currentSale;

    let rows;
    if (currentSale.SaleLines && currentSale.SalePayments) {
      const saleLines = Array.isArray(currentSale.SaleLines.SaleLine)
        ? currentSale.SaleLines.SaleLine
        : [currentSale.SaleLines.SaleLine];
      const salePayments = Array.isArray(currentSale.SalePayments.SalePayment)
        ? currentSale.SalePayments.SalePayment
        : [currentSale.SalePayments.SalePayment];

      rows = saleLines.reduce((dataStruct: TSFixMe, currentrow: TSFixMe) => {
        const {
          itemID,
          unitQuantity,
          saleID,
          discountID,
          discountPercent,
          displayableUnitPrice,
          tax1Rate,
          calcTax1,
          calcSubtotal,
          calcTotal,
          tax,
          Item,
          Note,
          Discount,
        } = currentrow;

        const description = Number(itemID) > 0 ? Item.description : Note.note;

        const isDiscountFixed = Discount?.discountAmount !== '0';
        let discount = 0;
        if (discountID !== '0' && Discount) {
          discount = isDiscountFixed
            ? Number.parseFloat(Discount.discountAmount) // Fixed discount
            : Number(displayableUnitPrice) * unitQuantity * Number.parseFloat(discountPercent); // Percent discount
        }

        const data = {
          date: localizeDate(completeTime, dateTimeFormat, shopTimeZone),
          // description sometimes have new line characters, so replace with comma.
          description,
          discount: toMoney(discount, currencyCode, localeCode),
          isTaxed: tax,
          netOfVat: toMoney(calcSubtotal, currencyCode, localeCode),
          paymentDate: localizeDate(
            salePayments[0].createTime,
            dateTimeFormat,
            shopTimeZone,
            // @ts-expect-error ts-migrate(2554) FIXME: Expected 3 arguments, but got 4.
            localeCode
          ),
          saleID,
          shopName,
          subtotal: toMoney(calcSubtotal, currencyCode, localeCode),
          taxPoint: localizeDate(completeTime, dateTimeFormat, shopTimeZone),
          total: toMoney(calcTotal, currencyCode, localeCode),
          totalTax: toMoney(calcTax1, currencyCode, localeCode),
          unitSold: unitQuantity,
          vatRate: tax1Rate,
        };

        const filteredReport = filterJSON(REPORT_FILTERS[reportName], data);

        dataStruct.push(filteredReport);
        return dataStruct;
      }, []);
    } else {
      return processedSales;
    }
    processedSales.push(...rows);
    return processedSales;
  }, []);
};

// format for better file name
export const currentDate = () =>
  `${new Date()
    .toLocaleString('en-CA', { hour12: false })
    .split(',')
    .map((string) => string.trim())
    .join('_')
    .replace(/\//g, '-')}`;

export const blob = (csv: TSFixMe) => {
  const blobParts = Modules.hasModule('bulk.export.excel_csv')
    ? [new Uint8Array([0xef, 0xbb, 0xbf])] // UTF-8 BOM
    : [];

  blobParts.push(csv);

  return new Blob(blobParts, { type: 'text/csv;charset=utf-8;' });
};
